import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Send as SubmitIcon,
  AttachMoney as ReimburseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

// Components
import { Button } from '../../../design-system';
import MagicLoading from '../../../components/ui/MagicLoading';
import MagicFileUpload from '../../../components/ui/FormFields/MagicFileUpload';
import EntityListContainer from '../../../components/common/EntityListContainer';
import EntityStatusBadge from '../../../components/core/EntityStatusBadge';
import { EntityCard } from '../../../design-system/components/data';
import ActionsMenu from '../../../components/ui/ActionsMenu';
import useExpenseStore from '../../../stores/expenseStore';
import useAuthStore from '../../../stores/authStore';
import useConfirmationDialog from '../../../hooks/useConfirmationDialog';
import useUIStore from '../../../stores/uiStore';

const ExpenseList = () => {
  const navigate = useNavigate();
  
  // Store hooks (following the exact pattern of Brands component)
  const { 
    expenses, 
    meta, 
    isLoading, 
    error, 
    fetchExpenses,
    deleteExpense,
    approveExpense,
    rejectExpense,
    exportToExcel,
    importFromExcel
  } = useExpenseStore();
  
  const { user } = useAuthStore();
  const { addNotification } = useUIStore();
  const [ConfirmDialog, confirm] = useConfirmationDialog();
  
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionComment, setActionComment] = useState('');

  // Create fetchData callback like Brands component
  const fetchData = useCallback((params) => {
    return fetchExpenses(params);
  }, [fetchExpenses]);

  const handleView = (expenseId) => {
    navigate(`/finances/expenses/${expenseId}`);
  };

  const handleEdit = (expenseId) => {
    navigate(`/finances/expenses/${expenseId}/edit`);
  };

  const handleDelete = async (expense) => {
    const confirmed = await confirm({
      title: 'Delete Expense?',
      content: 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });

    if (confirmed) {
      try {
        await deleteExpense(expense._id);
        addNotification({
          message: 'Expense deleted successfully',
          type: 'success'
        });
      } catch (error) {
        addNotification({
          message: 'Failed to delete expense',
          type: 'error'
        });
      }
    }
  };

  const handleSubmitForApproval = (expense) => {
    setSelectedExpense(expense);
    setActionType('submit');
    setActionDialogOpen(true);
  };

  const handleApprove = (expense) => {
    setSelectedExpense(expense);
    setActionType('approve');
    setActionDialogOpen(true);
  };

  const handleReject = (expense) => {
    setSelectedExpense(expense);
    setActionType('reject');
    setActionDialogOpen(true);
  };

  const handleActionSubmit = async () => {
    try {
      if (actionType === 'approve') {
        await approveExpense(selectedExpense._id, actionComment);
        addNotification({
          message: 'Expense approved',
          type: 'success'
        });
      } else if (actionType === 'reject') {
        await rejectExpense(selectedExpense._id, actionComment);
        addNotification({
          message: 'Expense rejected',
          type: 'success'
        });
      }
      setActionDialogOpen(false);
    } catch (error) {
      addNotification({
        message: `Failed to ${actionType} expense`,
        type: 'error'
      });
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      addNotification({
        message: 'Please select a file to import',
        type: 'error'
      });
      return;
    }

    try {
      const result = await importFromExcel(selectedFile);
      addNotification({
        message: `Imported ${result.imported.length} expenses successfully`,
        type: 'success'
      });
      if (result.errors.length > 0) {
        addNotification({
          message: `${result.errors.length} expenses failed to import`,
          type: 'warning'
        });
      }
      setImportDialogOpen(false);
      setSelectedFile(null);
    } catch (error) {
      addNotification({
        message: 'Failed to import expenses',
        type: 'error'
      });
    }
  };

  const getActionMenuItems = (expense) => {
    const items = [
      {
        label: 'View',
        icon: <ViewIcon />,
        onClick: () => handleView(expense._id)
      }
    ];

    // Add actions based on expense status and user permissions
    if (expense.status === 'draft' && expense.owner._id === user._id) {
      items.push(
        {
          label: 'Edit',
          icon: <EditIcon />,
          onClick: () => handleEdit(expense._id)
        },
        {
          label: 'Submit for Approval',
          icon: <SubmitIcon />,
          onClick: () => handleSubmitForApproval(expense)
        },
        {
          label: 'Delete',
          icon: <DeleteIcon />,
          onClick: () => handleDelete(expense),
          color: 'error'
        }
      );
    }

    if (expense.status === 'pending' && expense.currentApprover?._id === user._id) {
      items.push(
        {
          label: 'Approve',
          icon: <ApproveIcon />,
          onClick: () => handleApprove(expense),
          color: 'success'
        },
        {
          label: 'Reject',
          icon: <RejectIcon />,
          onClick: () => handleReject(expense),
          color: 'error'
        }
      );
    }

    return items;
  };

  // Memoize table columns to prevent unnecessary re-renders
  const tableColumns = useMemo(() => [
    { 
      id: 'expenseNumber', 
      label: 'Expense #',
      renderCell: (expense) => (
        <Typography variant="body2" fontWeight={500}>
          {expense.expenseNumber}
        </Typography>
      )
    },
    { 
      id: 'vendor', 
      label: 'Vendor',
      renderCell: (expense) => (
        <Typography variant="body2">
          {expense.vendor}
        </Typography>
      )
    },
    { 
      id: 'category', 
      label: 'Category',
      align: 'center',
      renderCell: (expense) => (
        <Chip
          label={expense.category.replace('_', ' ').toUpperCase()}
          size="small"
          variant="outlined"
        />
      )
    },
    { 
      id: 'amount', 
      label: 'Amount',
      align: 'right',
      renderCell: (expense) => (
        <Typography variant="body1" fontWeight={500}>
          ${expense.amount.toFixed(2)}
        </Typography>
      )
    },
    { 
      id: 'taxAmount', 
      label: 'Tax',
      align: 'right',
      renderCell: (expense) => (
        <Typography variant="body2" color="textSecondary">
          ${(expense.taxAmount || 0).toFixed(2)}
        </Typography>
      )
    },
    { 
      id: 'totalAmount', 
      label: 'Total',
      align: 'right',
      renderCell: (expense) => (
        <Typography variant="body1" fontWeight={600} color="primary">
          ${(expense.totalAmount || expense.amount || 0).toFixed(2)}
        </Typography>
      )
    },
    { 
      id: 'date', 
      label: 'Date',
      align: 'center',
      renderCell: (expense) => (
        <Typography variant="body2">
          {format(new Date(expense.date), 'MMM dd, yyyy')}
        </Typography>
      )
    },
    { 
      id: 'status', 
      label: 'Status',
      align: 'center',
      renderCell: (expense) => (
        <EntityStatusBadge entity="expense" status={expense.status} />
      )
    },
    { 
      id: 'actions', 
      label: 'Actions',
      align: 'center',
      renderCell: (expense) => (
        <ActionsMenu
          items={getActionMenuItems(expense)}
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && selectedExpense?._id === expense._id}
          onOpen={(event) => {
            setAnchorEl(event.currentTarget);
            setSelectedExpense(expense);
          }}
          onClose={() => {
            setAnchorEl(null);
            setSelectedExpense(null);
          }}
        />
      )
    }
  ], [user._id, anchorEl, selectedExpense]);

  const menuItems = useMemo(() => [
    {
      label: actionType === 'approve' ? 'Approve' : 'Reject',
      icon: actionType === 'approve' ? <ApproveIcon /> : <RejectIcon />,
      onClick: handleActionSubmit,
      color: actionType === 'approve' ? 'success' : 'error'
    }
  ], [actionType]);

  return (
    <Box>
      <EntityListContainer
        title="Expenses"
        description="Manage your business expenses"
        fetchData={fetchData}
        renderEntity={(expense) => (
          <EntityCard
            title={expense.expenseNumber}
            description={expense.vendor}
            status={
              <EntityStatusBadge entity="expense" status={expense.status} />
            }
            chips={[
              <Chip
                key="category"
                label={expense.category.replace('_', ' ').toUpperCase()}
                size="small"
                variant="outlined"
              />,
              expense.billable && (
                <Chip
                  key="billable"
                  label="Billable"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )
            ]}
            stats={[
              { label: 'Amount', value: `$${expense.amount.toFixed(2)}` },
              { label: 'Tax', value: `$${(expense.taxAmount || 0).toFixed(2)}` },
              { label: 'Total', value: `$${(expense.totalAmount || expense.amount || 0).toFixed(2)}` },
              { label: 'Date', value: format(new Date(expense.date), 'MMM dd, yyyy') }
            ]}
            linkTo={`/finances/expenses/${expense._id}`}
          />
        )}
        entities={expenses}
        isLoading={isLoading}
        metadata={meta}
        error={error}
        tableConfig={{
          columns: tableColumns,
          onRowClick: (expense) => navigate(`/finances/expenses/${expense._id}`),
          renderRowActions: (expense) => (
            <ActionsMenu
              items={getActionMenuItems(expense)}
            />
          )
        }}
        initialFilters={{ category: 'all', billable: 'all' }}
        initialSort={{ field: 'date', direction: 'desc' }}
        availableViews={['grid', 'table']}
        defaultView="table"
        actions={{
          canCreate: true,
          createPath: '/finances/expenses/create',
          createLabel: 'Add Expense',
          extraActions: (
            <>
              <Button
                startIcon={<ImportIcon />}
                onClick={() => setImportDialogOpen(true)}
                variant="outlined"
              >
                Import
              </Button>
              <Button
                startIcon={<ExportIcon />}
                onClick={exportToExcel}
                variant="outlined"
              >
                Export
              </Button>
            </>
          )
        }}
        filterConfig={[
          {
            name: 'category',
            label: 'Category',
            allLabel: 'All Categories',
            options: [
              { value: 'travel', label: 'Travel' },
              { value: 'supplies', label: 'Supplies' },
              { value: 'software', label: 'Software' },
              { value: 'marketing', label: 'Marketing' },
              { value: 'office', label: 'Office' },
              { value: 'meals', label: 'Meals & Entertainment' },
              { value: 'professional', label: 'Professional Services' },
              { value: 'other', label: 'Other' }
            ]
          },
          {
            name: 'billable',
            label: 'Billable',
            allLabel: 'All Expenses',
            options: [
              { value: true, label: 'Billable' },
              { value: false, label: 'Non-Billable' }
            ]
          }
        ]}
        sortOptions={[
          { field: 'date', label: 'Date' },
          { field: 'amount', label: 'Amount' },
          { field: 'vendor', label: 'Vendor' },
          { field: 'category', label: 'Category' }
        ]}
        searchFields={['expenseNumber', 'vendor', 'description']}
      />

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Import Expenses</DialogTitle>
        <DialogContent>
          <MagicFileUpload
            value={selectedFile}
            onChange={(files) => setSelectedFile(files[0])}
            accept=".xlsx,.xls"
            label="Select Excel file"
            helperText="Upload an Excel file with expense data"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleImport} variant="contained">
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Expense' : 'Reject Expense'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            fullWidth
            multiline
            rows={3}
            value={actionComment}
            onChange={(e) => setActionComment(e.target.value)}
            placeholder={`Add a comment for ${actionType}ing this expense`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleActionSubmit} 
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
          >
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog />
    </Box>
  );
};

export default ExpenseList;